import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { LoaderSymbol } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm } from '../../components/confirm';
import { useAuthStore } from '../../auth/store';
import { useNavPermission } from '../../hooks/useNavPermission';
import { fmtLocalDateTime } from '../../utils/datetime';
import { ComboSelect } from '../../components/ComboSelect';
import './sampleIn.css';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface ToOptions { storages: string[]; users: string[]; }

/** Amber "required to proceed" highlight for the scan input + destination select
 *  (matches the Sample-Tracking prototype's orange-tinted Barcode Scan / To fields
 *  and the app's required-field tint, §18.6 rule 12). */
const REQ_FIELD: CSSProperties = {
  border: '1px solid #ff8c42', borderRadius: 4, padding: '8px 10px',
  fontSize: 13, background: '#fffbe7', color: 'var(--ai-ink)',
};

/** Prototype "Barcode Transaction" section-header colours — the blue gradient
 *  band (--blue-hd1 → --blue-hd2), blue-ink text, orange section icon. */
const SECTION_HDR: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px',
  fontWeight: 800, fontSize: 13, letterSpacing: '.3px', color: '#234b70',
  background: 'linear-gradient(180deg,#eaf2fb,#d7e6f6)',
  borderBottom: '1px solid #b9cbe0',
};

interface ResolveResult {
  ok:             boolean;
  error:          string | null;
  sampleBottleId: string | null;
  sampleId:       string | null;
  jobId:          string | null;
  client:         string | null;
  sampleLoginOid: string | null;
  jobOid:         string | null;
}

/** One scanned-but-unsaved custody line held in the working grid. */
interface ScanRow {
  id:             string;   // client-only key
  sampleBottleId: string;
  jobId:          string;   // sent to the API; not shown (prototype's 7-col grid)
  client:         string;   // sent to the API; not shown
  from:           string;
  to:             string;
  scannedAt:      string;   // ISO — display only; server stamps the real DateHandled
  handledBy:      string;
  hazardous:      boolean;
  mode:           string;
  sampleLoginOid: string | null;
  jobOid:         string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component — ALPACA-parity Barcode Sample Custody → Sample In entry form,
// laid out to match the Sample-Tracking module prototype: top + bottom Save/Cancel
// bars, a Barcode Scan + To band, and the "Barcode Transaction" grid.
// (CLAUDE.md §18.11b — legacy SampleInOutController, SampleCustodyTest_DetailView_SampleIn.)
// ─────────────────────────────────────────────────────────────────────────────
export default function SampleInPage() {
  const [opts,     setOpts]     = useState<ToOptions>({ storages: [], users: [] });
  const [to,       setTo]       = useState('');
  const [barcode,  setBarcode]  = useState('');
  const [rows,     setRows]     = useState<ScanRow[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [scanning, setScanning] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [loadMs,   setLoadMs]   = useState<number | null>(null);
  const [loading,  setLoading]  = useState(true);

  const barcodeRef = useRef<HTMLInputElement>(null);
  const notify   = useNotify();
  const runSave  = useSaveFlow();
  const confirm  = useConfirm();
  const userName = useAuthStore(s => s.user?.name ?? '');
  // MAC write gate (ALPACA parity): Save enabled only when the role is granted
  // Write on the "Sample In" NavigationItem. Server enforces the same on POST.
  const perm = useNavPermission('Sample In');
  const canWrite = perm.canWrite || perm.isAdmin;

  // ── Load the "To" destination options once ──────────────────────────────────
  useEffect(() => {
    let alive = true;
    const t0 = performance.now();
    (async () => {
      try {
        const res = await api.get('/api/v1/sample-custody/to-options');
        if (alive) setOpts((res.data as { data: ToOptions }).data ?? { storages: [], users: [] });
      } catch {
        if (alive) notify.error('Failed to load destinations.');
      } finally {
        if (alive) { setLoadMs(performance.now() - t0); setLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, [notify]);

  // ── Scan one barcode: validate, resolve, add a working row (ALPACA
  //    ObjectSpace_ObjectChanged "In" branch). Auto-fires when the barcode field
  //    commits (blur / Enter / scanner terminator) OR the To changes — NOT only
  //    on Enter — matching ALPACA's ImmediatePostData editors. `code`/`toVal` are
  //    passed explicitly so the To-change handler can use the just-picked value. ─
  const scanBusy = useRef(false);
  const scanCode = useCallback(async (rawCode: string, toVal: string) => {
    const code = (rawCode ?? '').trim();
    if (!code || scanBusy.current) return;
    // ALPACA: "To" is required before a scan is accepted.
    if (!toVal) { notify.error("Column 'To' should not be empty."); return; }
    // Already in the current (unsaved) batch.
    if (rows.some(r => r.sampleBottleId.toUpperCase() === code.toUpperCase())) {
      notify.error("This 'Bottle ID' exists already. Please enter a new 'Bottle ID'.");
      setBarcode('');
      return;
    }
    scanBusy.current = true;
    setScanning(true);
    try {
      const res = await api.post('/api/v1/sample-custody/resolve', { bottleId: code });
      const r = (res.data as { data: ResolveResult }).data;
      if (!r.ok) { notify.error(r.error ?? "Please enter a valid 'Bottle ID'."); setBarcode(''); return; }
      setRows(prev => [
        {
          id:             `${code}-${prev.length}-${performance.now()}`,
          sampleBottleId: r.sampleBottleId ?? code.toUpperCase(),
          jobId:          r.jobId ?? '',
          client:         r.client ?? '',
          from:           userName,
          to:             toVal,
          scannedAt:      new Date().toISOString(),
          handledBy:      userName,
          hazardous:      false,
          mode:           'In',
          sampleLoginOid: r.sampleLoginOid,
          jobOid:         r.jobOid,
        },
        ...prev,
      ]);
      setBarcode('');
    } catch {
      notify.error('Scan failed. Please try again.');
    } finally {
      scanBusy.current = false;
      setScanning(false);
      barcodeRef.current?.focus();
    }
  }, [rows, userName, notify]);

  // ── Unlink — remove the selected (unsaved) rows from the working grid. ───────
  const unlink = useCallback(async () => {
    if (selected.size === 0) { notify.warning('Select a scanned row to unlink.'); return; }
    const ok = await confirm.confirm({
      title: 'Unlink',
      message: `Remove ${selected.size} scanned bottle(s) from this batch?`,
      tone: 'danger',
    });
    if (!ok) return;
    setRows(prev => prev.filter(r => !selected.has(r.id)));
    setSelected(new Set());
    notify.success("Sample 'Bottle ID' unlinked successfully.");
  }, [selected, confirm, notify]);

  // ── Save — the shared flow ("Do you want to Save?" → persist → "Saved
  //    successfully.") then clear the grid for the next batch. Create is
  //    reason-exempt (auditAction 'create'). ─────────────────────────────────
  const save = useCallback(() => {
    if (rows.length === 0) { notify.error("Please scan the sample 'Bottle ID'."); return; }
    setSaving(true);
    runSave({
      formName: 'Sample In',
      auditAction: 'create',
      save: async () => {
        await api.post('/api/v1/sample-custody/sample-in', {
          to,
          scans: rows.map(r => ({
            sampleBottleId: r.sampleBottleId,
            sampleLoginOid: r.sampleLoginOid,
            jobOid:         r.jobOid,
            client:         r.client,
          })),
        });
        setRows([]);
        setSelected(new Set());
      },
      successMessage: `Saved ${rows.length} bottle(s) to Sample In.`,
    }).finally(() => setSaving(false));
  }, [rows, to, runSave, notify]);

  // ── Cancel — discard the current (unsaved) batch. ───────────────────────────
  const cancel = useCallback(async () => {
    if (rows.length > 0) {
      const ok = await confirm.confirm({
        title: 'Cancel', message: `Discard ${rows.length} scanned bottle(s)?`, tone: 'danger',
      });
      if (!ok) return;
    }
    setRows([]); setSelected(new Set()); setBarcode('');
  }, [rows, confirm]);

  // ── "To" options — one flat, alphabetical destination list (ALPACA parity:
  //    storages + users from GetToValues, merged and sorted). ──────────────────
  const toOptions = useMemo(
    () => [...opts.storages, ...opts.users]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [opts]);

  // ── Grid columns — the prototype's 7-column "Barcode Transaction" grid ───────
  const cols: AppColumn<ScanRow>[] = useMemo(() => [
    { field: 'sampleBottleId', header: 'SampleBottleID', width: 170 },
    { field: 'from',           header: 'From',           width: 130 },
    { field: 'to',             header: 'To',             width: 150 },
    { field: 'scannedAt',      header: 'DateHandled',    width: 160,
      text: r => fmtLocalDateTime(r.scannedAt) },
    { field: 'handledBy',      header: 'HandledBy',      width: 130 },
    { field: 'hazardous',      header: 'Hazardous',      width: 100,
      render: r => <input type="checkbox" checked={r.hazardous} disabled readOnly />,
      text: r => (r.hazardous ? 'Yes' : 'No') },
    { field: 'mode',           header: 'Mode', width: 90,
      render: r => <span className="bl-pill ok">{r.mode}</span>, text: r => r.mode },
  ], []);

  // ── The top/bottom Save · Cancel bar (prototype parity). ────────────────────
  const renderSaveBar = (bottom?: boolean) => (
    <div className="bl-card" style={{
      display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8,
      padding: '10px 12px', marginBottom: bottom ? 0 : 8, marginTop: bottom ? 8 : 0,
    }}>
      <button className="ca-btn ca-btn-solid" onClick={save}
        disabled={saving || rows.length === 0 || !canWrite}
        title={!canWrite ? 'You are not authorized to save on Sample In.' : 'Save'}>
        {saving ? <><LoaderSymbol size={13} /> Saving…</> : <><span className="tb-i">💾</span> Save</>}
      </button>
      <button className="ca-btn" onClick={() => void cancel()} disabled={saving} title="Cancel">
        <span className="tb-i" style={{ color: 'var(--ai-blue, #185fa5)' }}>↩</span> Cancel
      </button>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="bl-page">

      {/* Fixed top: breadcrumb + title */}
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sample Tracking</span>
            <i className="ti ti-chevron-right" />
            <span>Barcode Sample Custody</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Sample In</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="sample-in" loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            Pick a destination, then scan bottle barcodes to check them in · {rows.length} scanned
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bl-grid-area">

        {/* TOP Save · Cancel bar */}
        {renderSaveBar()}

        {/* Barcode Scan + To band */}
        <div className="bl-card" style={{ padding: 12, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '0 0 auto' }}>
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Barcode Scan</span>
              <input
                ref={barcodeRef}
                type="text"
                style={{ width: 380, maxWidth: '100%', flex: 'none', ...REQ_FIELD }}
                placeholder="Scan or type a bottle barcode…"
                value={barcode}
                disabled={scanning}
                onChange={e => setBarcode(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void scanCode(barcode, to); } }}
                onBlur={() => { if (barcode.trim() && to) void scanCode(barcode, to); }}
              />
              {scanning && <LoaderSymbol size={14} />}
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '0 0 auto' }}>
              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>To</span>
              <div style={{ width: 360, maxWidth: '100%' }}>
                {/* Shared ComboSelect — compact scrollable popup + type-to-search,
                    flat destination list (ALPACA parity). */}
                <ComboSelect
                  className="si-to-combo"
                  value={to}
                  options={toOptions}
                  onChange={(v) => { setTo(v); if (barcode.trim() && v) void scanCode(barcode, v); }}
                  placeholder="— Select destination —"
                />
              </div>
            </label>
          </div>
        </div>

        {/* Barcode Transaction section: header + toolbar + grid */}
        <div className="bl-card bl-card-flex" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={SECTION_HDR}>
            <span style={{ color: '#f97316' }}>📦</span> Barcode Transaction
            <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#234b70' }}>
              Count = {rows.length}
            </span>
          </div>
          <div style={{ padding: 10 }}>
            <div style={{ marginBottom: 8 }}>
              <AppToolbar
                left={
                  <button className="ca-btn" onClick={() => void unlink()} disabled={selected.size === 0} title="Unlink selected">
                    <span className="tb-i">🔗</span> Unlink{selected.size > 0 ? ` (${selected.size})` : ''}
                  </button>
                }
                exportData={{ filename: 'sample-in', ...gridExportMatrix(rows, cols) }}
              />
            </div>
            <AppDataGrid<ScanRow>
              rows={rows}
              columns={cols}
              getRowId={r => r.id}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              emptyText="No data to display — scan a barcode above."
            />
          </div>
        </div>

        {/* BOTTOM Save · Cancel bar (legacy parity) */}
        {renderSaveBar(true)}
      </div>
    </div>
  );
}
